import React, { useState, useEffect, lazy, Suspense } from 'react';

// Redux
import { useSelector, useDispatch } from 'react-redux';

// Material UI
import Grid from '@material-ui/core/Grid';
import List from '@material-ui/core/List';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';

// Date FNS
import format from 'date-fns/format';

// Clsx
import clsx from 'clsx';

// Constants
import FACIAL from 'constants/facial';
import CONST_FLEET from 'constants/fleet';
import CONST_STOPS from 'constants/stops';
import CONST_LINES from 'constants/lines';
import SECTIONS from 'constants/sections';
import ITEMS from 'constants/itemsByFilter';
import PROFILES_CONST from 'constants/profiles';
import CONST_COLLECTION from 'constants/collection';
import AMOUNT_OF_DAYS from 'constants/amountOfDays';
import TRANSPORT_CARD_CONST from 'constants/transportCards';
import CONST_SELLING_POINTS from 'constants/sellingPoints';
import DATE_TYPE_SALES_REPORT from 'constants/dateTypeSalesReport';
import LINE_TYPE_SALES_REPORT from 'constants/lineTypeSalesReport';
import TRIP_OR_TICKET from 'constants/tripOrTicket';
import CONST_TRANSPORT_VOUCHER_COLLECTION from 'constants/transportVoucherCollectionReport';
import INVOICES_CONST from 'constants/invoices';
import EMV from 'constants/emvStatus';
import TITLES from 'constants/titles';
import DEVICE_RELEASE_STATUS from 'constants/deviceReleaseStatus';
import KM_TYPE from 'constants/kmType';
import BPE from 'constants/bpe';

// Helpers
import { maskCNPJ, maskPhoneDefault } from 'helpers/maskers';

// Actions
import {
  setSelectedProfiles,
  setSelectedName,
  setSelectedBpeCode,
  setSelectedBpeNumber,
  setSelectedIncludeBpe,
  setSelectedOrigin,
  setSelectedDestiny,
  setSelectedLineSectionName,
  setSelectedBenefit,
  setSelectedDocument,
  setSelectedEmail,
  setSelectedStartedAt,
  setSelectedCNPJ,
  setSelectedNSU,
  setSelectedPhone,
  setSelectedProfileExpiration,
  setSelectedProfileStatus,
  setSelectedStatus,
  setSelectedTransportCard,
  setSelectedTotalLines,
  setSelectedTitle,
  setSelectedTicketer,
  setSelectedTerminalName,
  setSelectedTariff,
  setSelectedSpecialBusStop,
  setSelectedSitCapacity,
  setSelectedResponsible,
  setSelectedRegistry,
  setSelectedPrefix,
  setSelectedNumber,
  setSelectedLot,
  setSelectedLineModes,
  setSelectedLineModel,
  setSelectedLicenseNumber,
  setSelectedLicense,
  setSelectedFootCapacity,
  setSelectedCovered,
  setSelectedCarModel,
  setSelectedCardType,
  setSelectedCardStatus,
  setSelectedCardNumber,
  setSelectedInvoiceStatus,
  setSelectedAge,
  setSelectedBusBay,
  setSelectedBlockName,
  setSelectedAirConditioned,
  setSelectedAccessible,
  setSelectedNumberOfDays,
  setSelectedCars,
  setSelectedCreditType,
  setSelectedDateType,
  setSelectedLineType,
  setSelectedStatusDeviceRelease,
  setSelectedEmployees,
  setSelectedDriverRegistry,
  setSelectedLines,
  setSelectedSchools,
  setSelectedPayments,
  setSelectedStartDate,
  setSelectedEndDate,
  setSelectedQRCodes,
  setSelectedReceivePayments,
  setSelectedTripStatus,
  setSelectedTripOrTicket,
  setSeccionedLines,
  setSelectedSellingPoint,
  setSelectedSellingTerminal,
  setSelectedSupervisors,
  setSelectedTokenStatus,
  setSelectedQuantity,
  setSelectedDbds,
  setFilteredItems,
  setSelectedFilename,
  setSelectedPaymentStatus,
  setSelectedOperationType,
  setSelectedIncomingTypes,
  setSelectedDevice,
  setSelectedCreditCard,
  setSelectedTerminals,
  setSelectedEmvType,
  setSelectedBrand,
  setSelectedEmvStatus,
  setSelectedSchoolCode,
  setSelectedSchoolType,
  setSelectedCompanies,
  setSelectedPaymentDay,
  setSelectedDateTypeBPe,
  setSelectedTicketIdCode,
  setSelectedSaleIdCode,
  setSelectedAddIdleKm,
} from 'store/ducks/reports/actions';
import {
  setSelectedApprovalStatus,
  setSelectedStatus as setSelectedFacialStatus,
  setSelectedScore,
  setSelectedValidationStatus,
} from 'store/ducks/facial/actions';
import { changeKmType } from 'store/ducks/trips/actions';
import { setToggleVoucher } from 'store/ducks/voucher/actions';
import { changeBalanceType } from 'store/ducks/balance/actions';
import { setSelectedSearchUser } from 'store/ducks/users/actions';
import { setSelectedCollection } from 'store/ducks/employees/actions';
import {
  setSelectedPaymentType,
  setSelectedStatus as setSelectedCompanyStatus,
  setSelectedType as setSelectedCompanyType,
} from 'store/ducks/companies/actions';
import { setSelectedReceivingType } from 'store/ducks/sellingTerminals/actions';

import { setClose } from 'store/ducks/dialogs/actions';

// Types
import { ApplicationState } from 'store';

// Components
import CloseIcon from 'components/Icons/Close';
import { ModalTemplate } from 'components/ModalTemplate';
import ListItem from './components/ListItem';

// Styles
import useStyles from './styles';

// Lazy Components
const componentsMap = {
  name: lazy(() => import('./components/Fields/Name')),
  accessible: lazy(() => import('./components/Fields/Accessible')),
  airConditioned: lazy(() => import('./components/Fields/AirConditioned')),
  blockName: lazy(() => import('./components/Fields/BlockName')),
  busBay: lazy(() => import('./components/Fields/BusBay')),
  age: lazy(() => import('./components/Fields/CarAge')),
  cardNumber: lazy(() => import('./components/Fields/CardNumber')),
  cardStatus: lazy(() => import('./components/Fields/CardStatus')),
  cardType: lazy(() => import('./components/Fields/CardType')),
  model: lazy(() => import('./components/Fields/CarModel')),
  covered: lazy(() => import('./components/Fields/Covered')),
  footCapacity: lazy(() => import('./components/Fields/FootCapacity')),
  license: lazy(() => import('./components/Fields/License')),
  licenseNumber: lazy(() => import('./components/Fields/LicenseNumber')),
  lineModel: lazy(() => import('./components/Fields/LineModel')),
  lineModes: lazy(() => import('./components/Fields/LineModes')),
  status: lazy(() => import('./components/Fields/LineStatus')),
  invoicesListStatus: lazy(() =>
    import('./components/Fields/InvoicesListStatus'),
  ),
  lot: lazy(() => import('./components/Fields/Lot')),
  number: lazy(() => import('./components/Fields/Number')),
  prefix: lazy(() => import('./components/Fields/Prefix')),
  registry: lazy(() => import('./components/Fields/Registry')),
  responsible: lazy(() => import('./components/Fields/Responsible')),
  sitCapacity: lazy(() => import('./components/Fields/SitCapacity')),
  specialBusStop: lazy(() => import('./components/Fields/SpecialBusStop')),
  tariff: lazy(() => import('./components/Fields/Tariff')),
  terminalName: lazy(() => import('./components/Fields/TerminalName')),
  ticketer: lazy(() => import('./components/Fields/Ticketer')),
  title: lazy(() => import('./components/Fields/Title')),
  totalLines: lazy(() => import('./components/Fields/TotalLines')),
  cnpj: lazy(() => import('./components/Fields/CNPJ')),
  nsu: lazy(() => import('./components/Fields/Nsu')),
  phone: lazy(() => import('./components/Fields/Phone')),
  profileStatus: lazy(() => import('./components/Fields/ProfileStatus')),
  document: lazy(() => import('./components/Fields/Document')),
  transportcard: lazy(() => import('./components/Fields/TransportCard')),
  email: lazy(() => import('./components/Fields/Email')),
  createdAt: lazy(() => import('./components/Fields/CreatedAt')),
  profile: lazy(() => import('./components/Fields/Profile')),
  period: lazy(() => import('./components/Fields/Period')),
  validationStatus: lazy(() => import('./components/Fields/ValidationStatus')),
  user: lazy(() => import('./components/Fields/User')),
  profileExpiration: lazy(() =>
    import('./components/Fields/ProfileExpiration'),
  ),
  day: lazy(() => import('./components/Fields/WeekDay')),
  score: lazy(() => import('./components/Fields/Score')),
  line: lazy(() => import('./components/Fields/Line')),
  lines: lazy(() => import('./components/Fields/Lines')),
  schools: lazy(() => import('./components/Fields/Schools')),
  facialStatus: lazy(() => import('./components/Fields/FacialStatus')),
  approvalStatus: lazy(() => import('./components/Fields/ApprovalStatus')),
  numberOfDays: lazy(() => import('./components/Fields/AmountOfDays')),
  balanceType: lazy(() => import('./components/Fields/BalanceType')),
  cars: lazy(() => import('./components/Fields/Cars')),
  collection: lazy(() => import('./components/Fields/Collection')),
  creditType: lazy(() => import('./components/Fields/CreditType')),
  dateType: lazy(() => import('./components/Fields/DateType')),
  dateTypeSales: lazy(() => import('./components/Fields/DateTypeSales')),
  dbd: lazy(() => import('./components/Fields/Dbd')),
  filename: lazy(() => import('./components/Fields/Filename')),
  supervisor: lazy(() => import('./components/Fields/Supervisor')),
  payment: lazy(() => import('./components/Fields/Payment')),
  paymentType: lazy(() => import('./components/Fields/PaymentType')),
  operationType: lazy(() => import('./components/Fields/OperationType')),
  paymentStatus: lazy(() => import('./components/Fields/PaymentStatus')),
  qrCode: lazy(() => import('./components/Fields/QRCode')),
  lineType: lazy(() => import('./components/Fields/LineType')),
  statusDeviceRelease: lazy(() =>
    import('./components/Fields/DeviceReleaseStatus'),
  ),
  receive: lazy(() => import('./components/Fields/Receive')),
  receivingType: lazy(() => import('./components/Fields/ReceivingType')),
  seccionedLines: lazy(() => import('./components/Fields/SeccionedLines')),
  sellingPoint: lazy(() => import('./components/Fields/SellingPoint')),
  sellingTerminal: lazy(() => import('./components/Fields/SellingTerminal')),
  tokenStatus: lazy(() => import('./components/Fields/TokenStatus')),
  usage: lazy(() => import('./components/Fields/Usage')),
  voucher: lazy(() => import('./components/Fields/Voucher')),
  employee: lazy(() => import('./components/Fields/Driver')),
  driverRegistry: lazy(() => import('./components/Fields/DriverRegistry')),
  kmType: lazy(() => import('./components/Fields/KmType')),
  addIdleKm: lazy(() => import('./components/Fields/AddIdleKm')),
  voucherCollectionStatus: lazy(() =>
    import('./components/Fields/VoucherCollectionStatus'),
  ),
  voucherCollectionType: lazy(() =>
    import('./components/Fields/VoucherCollectionType'),
  ),
  weekDay: lazy(() => import('./components/Fields/WeekDay')),
  incomingTypes: lazy(() => import('./components/Fields/IncomingTypes')),
  device: lazy(() => import('./components/Fields/Device')),
  creditCard: lazy(() => import('./components/Fields/CreditCard')),
  terminal: lazy(() => import('./components/Fields/Terminal')),
  emvType: lazy(() => import('./components/Fields/EmvType')),
  brand: lazy(() => import('./components/Fields/Brand')),
  emvStatus: lazy(() => import('./components/Fields/EmvStatus')),
  schoolCode: lazy(() => import('./components/Fields/SchoolCode')),
  schoolType: lazy(() => import('./components/Fields/SchoolType')),
  tripStatus: lazy(() => import('./components/Fields/TripStattus')),
  tripOrTicket: lazy(() => import('./components/Fields/TripOrTicket')),
  companies: lazy(() => import('./components/Fields/Companies')),
  bpeCode: lazy(() => import('./components/Fields/BpeCode')),
  bpeNumber: lazy(() => import('./components/Fields/BpeNumber')),
  origin: lazy(() => import('./components/Fields/Origin')),
  destiny: lazy(() => import('./components/Fields/Destiny')),
  lineSectionName: lazy(() => import('./components/Fields/LineSectionName')),
  benefit: lazy(() => import('./components/Fields/Benefit')),
  paymentDay: lazy(() => import('./components/Fields/PaymentDay')),
  includeBPe: lazy(() => import('./components/Fields/IncludeBpe')),
  dateTypeBPe: lazy(() => import('./components/Fields/DateTypeBPe')),
  ticketIdCode: lazy(() => import('./components/Fields/TicketIdCode')),
  saleIdCode: lazy(() => import('./components/Fields/SaleIdCode')),
};

type Props = {
  fields: Array<{ key: string; value: string }>;
  handleApply: (args: any) => void;
  notSetInitalDate?: boolean;
  notResetKmType?: boolean;
};

const Filter: React.FC<Props> = ({
  fields,
  handleApply,
  notSetInitalDate,
  notResetKmType,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [type, setType] = useState(fields?.[0]?.key || '');

  const selectedItems = useSelector(
    (state: ApplicationState) => state.groupingModal.selectedItems,
  );

  const selectedProfiles = useSelector(
    (state: ApplicationState) => state.reports.selectedProfiles,
  );

  const selectedProfileStatus = useSelector(
    (state: ApplicationState) => state.reports.selectedProfileStatus,
  );

  const selectedStatus = useSelector(
    (state: ApplicationState) => state.reports.selectedStatus,
  );

  const selectedProfileExpiration = useSelector(
    (state: ApplicationState) => state.reports.selectedProfileExpiration,
  );

  const selectedName = useSelector(
    (state: ApplicationState) => state.reports.selectedName,
  );

  const selectedBpeCode = useSelector(
    (state: ApplicationState) => state.reports.selectedBpeCode,
  );
  const selectedBpeNumber = useSelector(
    (state: ApplicationState) => state.reports.selectedBpeNumber,
  );
  const selectedTicketIdCode = useSelector(
    (state: ApplicationState) => state.reports.selectedTicketIdCode,
  );
  const selectedSaleIdCode = useSelector(
    (state: ApplicationState) => state.reports.selectedSaleIdCode,
  );

  const selectedIncludeBpe = useSelector(
    (state: ApplicationState) => state.reports.selectedIncludeBpe,
  );

  const selectedOrigin = useSelector(
    (state: ApplicationState) => state.reports.selectedOrigin,
  );

  const selectedDestiny = useSelector(
    (state: ApplicationState) => state.reports.selectedDestiny,
  );

  const selectedLineSectionName = useSelector(
    (state: ApplicationState) => state.reports.selectedLineSectionName,
  );

  const selectedBenefits = useSelector(
    (state: ApplicationState) => state.reports.selectedBenefits,
  );

  const selectedSchoolCode = useSelector(
    (state: ApplicationState) => state.reports.selectedSchoolCode,
  );

  const selectedSchoolType = useSelector(
    (state: ApplicationState) => state.reports.selectedSchoolType,
  );

  const selectedPhone = useSelector(
    (state: ApplicationState) => state.reports.selectedPhone,
  );

  const selectedCNPJ = useSelector(
    (state: ApplicationState) => state.reports.selectedCNPJ,
  );
  const selectedNSU = useSelector(
    (state: ApplicationState) => state.reports.selectedNSU,
  );
  const selectedDocument = useSelector(
    (state: ApplicationState) => state.reports.selectedDocument,
  );

  const selectedEmail = useSelector(
    (state: ApplicationState) => state.reports.selectedEmail,
  );

  const selectedTransportCard = useSelector(
    (state: ApplicationState) => state.reports.selectedTransportCard,
  );
  const selectedDevice = useSelector(
    (state: ApplicationState) => state.reports.selectedDevice,
  );

  const selectedCreditCard = useSelector(
    (state: ApplicationState) => state.reports.selectedCreditCard,
  );

  const startedAt = useSelector(
    (state: ApplicationState) => state.reports.startedAt || null,
  );

  const selectedTotalLines = useSelector(
    (state: ApplicationState) => state.reports.selectedTotalLines,
  );

  const selectedTitle = useSelector(
    (state: ApplicationState) => state.reports.selectedTitle,
  );

  const selectedTicketer = useSelector(
    (state: ApplicationState) => state.reports.selectedTicketer,
  );

  const selectedTerminalName = useSelector(
    (state: ApplicationState) => state.reports.selectedTerminalName,
  );

  const selectedTariff = useSelector(
    (state: ApplicationState) => state.reports.selectedTariff,
  );

  const selectedSpecialBusStop = useSelector(
    (state: ApplicationState) => state.reports.selectedSpecialBusStop,
  );

  const selectedSitCapacity = useSelector(
    (state: ApplicationState) => state.reports.selectedSitCapacity,
  );

  const selectedResponsible = useSelector(
    (state: ApplicationState) => state.reports.selectedResponsible,
  );

  const selectedRegistry = useSelector(
    (state: ApplicationState) => state.reports.selectedRegistry,
  );

  const selectedPrefix = useSelector(
    (state: ApplicationState) => state.reports.selectedPrefix,
  );

  const selectedNumber = useSelector(
    (state: ApplicationState) => state.reports.selectedNumber,
  );

  const selectedLot = useSelector(
    (state: ApplicationState) => state.reports.selectedLot,
  );

  const selectedLineModes = useSelector(
    (state: ApplicationState) => state.reports.selectedLineModes,
  );

  const selectedLineModel = useSelector(
    (state: ApplicationState) => state.reports.selectedLineModel,
  );

  const selectedLicenseNumber = useSelector(
    (state: ApplicationState) => state.reports.selectedLicenseNumber,
  );

  const selectedLicense = useSelector(
    (state: ApplicationState) => state.reports.selectedLicense,
  );

  const selectedFootCapacity = useSelector(
    (state: ApplicationState) => state.reports.selectedFootCapacity,
  );

  const selectedCovered = useSelector(
    (state: ApplicationState) => state.reports.selectedCovered,
  );

  const selectedCarModel = useSelector(
    (state: ApplicationState) => state.reports.selectedCarModel,
  );

  const selectedCardType = useSelector(
    (state: ApplicationState) => state.reports.selectedCardType,
  );

  const selectedCardStatus = useSelector(
    (state: ApplicationState) => state.reports.selectedCardStatus,
  );

  const selectedCardNumber = useSelector(
    (state: ApplicationState) => state.reports.selectedCardNumber,
  );

  const selectedInvoiceStatus = useSelector(
    (state: ApplicationState) => state.reports.selectedInvoiceStatus,
  );

  const selectedAge = useSelector(
    (state: ApplicationState) => state.reports.selectedAge,
  );

  const selectedBusBay = useSelector(
    (state: ApplicationState) => state.reports.selectedBusBay,
  );

  const selectedBlockName = useSelector(
    (state: ApplicationState) => state.reports.selectedBlockName,
  );

  const selectedAirConditioned = useSelector(
    (state: ApplicationState) => state.reports.selectedAirConditioned,
  );

  const selectedAccessible = useSelector(
    (state: ApplicationState) => state.reports.selectedAccessible,
  );

  const selectedNumberOfDays = useSelector(
    (state: ApplicationState) => state.reports.numberOfDays,
  );

  const selectedApprovalStatus = useSelector(
    (state: ApplicationState) => state.facial.selectedApprovalStatus,
  );

  const selectedBalanceType = useSelector(
    (state: ApplicationState) => state.balance.balanceType,
  );

  const selectedCollection = useSelector(
    (state: ApplicationState) => state.employees.selectedCollection,
  );

  const selectedCars = useSelector(
    (state: ApplicationState) => state.reports.selectedCars,
  );

  const selectedCreditType = useSelector(
    (state: ApplicationState) => state.reports.selectedCreditType,
  );

  const selectedDateType = useSelector(
    (state: ApplicationState) => state.reports.dateType,
  );

  const selectedDateTypeBPe = useSelector(
    (state: ApplicationState) => state.reports.dateTypeBPe,
  );

  const selectedLineType = useSelector(
    (state: ApplicationState) => state.reports.lineType,
  );

  const selectedTripOrTicket = useSelector(
    (state: ApplicationState) => state.reports.selectedTripOrTicket,
  );

  const selectedStatusDeviceRelease = useSelector(
    (state: ApplicationState) => state.reports.statusDeviceRelease,
  );

  const selectedDbds = useSelector(
    (state: ApplicationState) => state.reports.selectedDbds,
  );

  const selectedEmployees = useSelector(
    (state: ApplicationState) => state.reports.selectedEmployees,
  );

  const selectedDriverRegistry = useSelector(
    (state: ApplicationState) => state.reports.selectedDriverRegistry,
  );

  const selectedFacialStatus = useSelector(
    (state: ApplicationState) => state.facial.selectedStatus,
  );

  const selectedEmvStatus = useSelector(
    (state: ApplicationState) => state.reports.selectedEmvStatus,
  );

  const selectedFilename = useSelector(
    (state: ApplicationState) => state.reports.filenameSelected,
  );

  const selectedKmType = useSelector(
    (state: ApplicationState) => state.trips.kmType,
  );

  const selectedAddIdleKm = useSelector(
    (state: ApplicationState) => state.reports.addIdleKm,
  );

  const selectedLines = useSelector(
    (state: ApplicationState) => state.reports.selectedLines,
  );

  const selectedSchools = useSelector(
    (state: ApplicationState) => state.reports.selectedSchools,
  );

  const selectedPayments = useSelector(
    (state: ApplicationState) => state.reports.selectedPayments,
  );

  const selectedPaymentType = useSelector(
    (state: ApplicationState) => state.companies.selectedPaymentType,
  );

  const selectedPaymentStatus = useSelector(
    (state: ApplicationState) => state.reports.selectedPaymentStatus,
  );

  const selectedOperationType = useSelector(
    (state: ApplicationState) => state.reports.selectedOperationType,
  );

  const selectedStartDate = useSelector(
    (state: ApplicationState) => state.reports.selectedStartDate,
  );

  const selectedEndDate = useSelector(
    (state: ApplicationState) => state.reports.selectedEndDate,
  );

  const selectedQRCodes = useSelector(
    (state: ApplicationState) => state.reports.selectedQRCodes,
  );

  const selectedReceivePayments = useSelector(
    (state: ApplicationState) => state.reports.selectedReceivePayments,
  );

  const selectedTripStatus = useSelector(
    (state: ApplicationState) => state.reports.selectedTripStatus,
  );

  const selectedReceivingType = useSelector(
    (state: ApplicationState) => state.sellingTerminals.selectedReceivingType,
  );

  const selectedScore = useSelector(
    (state: ApplicationState) => state.facial.scorePercent,
  );

  const selectedSeccionedLine = useSelector(
    (state: ApplicationState) => state.reports.selectedSeccionedLine,
  );

  const selectedSellingPoint = useSelector(
    (state: ApplicationState) => state.reports.selectedSellingPoint,
  );

  const selectedSellingTerminal = useSelector(
    (state: ApplicationState) => state.reports.selectedSellingTerminal,
  );

  const selectedSupervisors = useSelector(
    (state: ApplicationState) => state.reports.selectedSupervisors,
  );

  const selectedTokenStatus = useSelector(
    (state: ApplicationState) => state.reports.selectedTokenStatus,
  );

  const selectedQuantity = useSelector(
    (state: ApplicationState) => state.reports.selectedQuantity,
  );

  const selectedSearchUser = useSelector(
    (state: ApplicationState) => state.users.selectedSearchUser,
  );

  const selectedValidationStatus = useSelector(
    (state: ApplicationState) => state.facial.selectedValidationStatus,
  );

  const selectedVoucher = useSelector(
    (state: ApplicationState) => state.voucher.voucher,
  );

  const selectedCompanyStatus = useSelector(
    (state: ApplicationState) => state.companies.selectedStatus,
  );

  const selectedCompanyType = useSelector(
    (state: ApplicationState) => state.companies.selectedType,
  );
  const selectedIncomingTypes = useSelector(
    (state: ApplicationState) => state.reports.selectedIncomingTypes,
  );

  const selectedTerminals = useSelector(
    (state: ApplicationState) => state.reports.selectedTerminals,
  );
  const selectedEmvType = useSelector(
    (state: ApplicationState) => state.reports.selectedEmvType,
  );
  const selectedBrand = useSelector(
    (state: ApplicationState) => state.reports.selectedBrand,
  );

  const filterItems = useSelector(
    (state: ApplicationState) => state.reports.filterItems,
  );

  const selectedCompanies = useSelector(
    (state: ApplicationState) => state.reports.selectedCompanies,
  );

  const selectedPaymentDay = useSelector(
    (state: ApplicationState) => state.reports.selectedPaymentDay,
  );

  const Component = (componentsMap as Record<string, any>)[type];

  const handleType = (type: string) => setType(type);

  useEffect(() => {
    if (!filterItems.length && !notSetInitalDate) {
      dispatch(
        setSelectedStartDate({
          selectedStartDate: new Date(new Date().setHours(0, 0, 0)),
        }),
      );
      dispatch(
        setSelectedEndDate({
          selectedEndDate: new Date(new Date().setHours(23, 59, 59)),
        }),
      );
      dispatch(
        setSelectedStartedAt({
          startedAt: new Date(),
        }),
      );
      const newData = {
        key: ITEMS.key.period,
        value: '',
      };
      dispatch(
        setFilteredItems({
          filterItems: filterItems
            .filter(({ key }) => key !== ITEMS.key.period)
            .concat(newData),
        }),
      );
    }
    if (!notResetKmType) {
      dispatch(
        changeKmType({
          kmType: '',
        }),
      );
    }
  }, []);

  const handleDeleteSelectedName = () => {
    dispatch(
      setSelectedName({
        selectedName: '',
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(({ key }) => key !== ITEMS.key.name),
      }),
    );
  };

  const handleDeleteSelectedBpeCode = () => {
    dispatch(
      setSelectedBpeCode({
        selectedBpeCode: '',
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(({ key }) => key !== ITEMS.key.bpeCode),
      }),
    );
  };
  const handleDeleteSelectedBpeNumber = () => {
    dispatch(
      setSelectedBpeNumber({
        selectedBpeNumber: '',
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.bpeNumber,
        ),
      }),
    );
  };

  const handleDeleteSelectedTicketIdCode = () => {
    dispatch(
      setSelectedTicketIdCode({
        selectedTicketIdCode: '',
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.ticketIdCode,
        ),
      }),
    );
  };

  const handleDeleteSelectedSaleIdCode = () => {
    dispatch(
      setSelectedSaleIdCode({
        selectedSaleIdCode: '',
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.saleIdCode,
        ),
      }),
    );
  };

  const handleDeleteSelectedIncludeBpe = () => {
    dispatch(
      setSelectedIncludeBpe({
        selectedIncludeBpe: '',
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.includeBPe,
        ),
      }),
    );
  };

  const handleDeleteSelectedOrigin = () => {
    dispatch(
      setSelectedOrigin({
        selectedOrigin: '',
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(({ key }) => key !== ITEMS.key.origin),
      }),
    );
  };

  const handleDeleteSelectedDestiny = () => {
    dispatch(
      setSelectedDestiny({
        selectedDestiny: '',
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(({ key }) => key !== ITEMS.key.destiny),
      }),
    );
  };
  const handleDeleteSelectedLineSectionName = () => {
    dispatch(
      setSelectedLineSectionName({
        selectedLineSectionName: '',
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.lineSectionName,
        ),
      }),
    );
  };
  const handleDeleteSelectedBenefits = () => {
    dispatch(
      setSelectedBenefit({
        selectedBenefits: [],
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(({ key }) => key !== ITEMS.key.benefit),
      }),
    );
  };

  const handleDeleteSelectedCNPJ = () => {
    dispatch(
      setSelectedCNPJ({
        selectedCNPJ: '',
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(({ key }) => key !== ITEMS.key.cnpj),
      }),
    );
  };
  const handleDeleteSelectedNSU = () => {
    dispatch(
      setSelectedNSU({
        selectedNSU: '',
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(({ key }) => key !== ITEMS.key.nsu),
      }),
    );
  };

  const handleDeleteSelectedPhone = () => {
    dispatch(
      setSelectedPhone({
        selectedPhone: '',
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(({ key }) => key !== ITEMS.key.phone),
      }),
    );
  };

  const handleDeleteSelectedProfile = () => {
    dispatch(
      setSelectedProfiles({
        selectedProfiles: [],
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(({ key }) => key !== ITEMS.key.profile),
      }),
    );
  };

  const handleDeleteSelectedProfileExpiration = () => {
    dispatch(
      setSelectedProfileExpiration({
        selectedProfileExpiration: null,
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.profileExpiration,
        ),
      }),
    );
  };

  const handleDeleteSelectedProfileStatus = () => {
    dispatch(
      setSelectedProfileStatus({
        selectedProfileStatus: null,
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.profileStatus,
        ),
      }),
    );
  };

  const handleDeleteSelectedStatus = () => {
    dispatch(
      setSelectedStatus({
        selectedStatus: null,
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(({ key }) => key !== ITEMS.key.status),
      }),
    );
  };

  const handleDeleteSelectedDocument = () => {
    dispatch(
      setSelectedDocument({
        selectedDocument: '',
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.document,
        ),
      }),
    );
  };

  const handleDeleteSelectedEmail = () => {
    dispatch(
      setSelectedEmail({
        selectedEmail: '',
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(({ key }) => key !== ITEMS.key.email),
      }),
    );
  };

  const handleDeleteSelectedTransportCard = () => {
    dispatch(
      setSelectedTransportCard({
        selectedTransportCard: '',
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.transportcard,
        ),
      }),
    );
  };
  const handleDeleteSelectedDevice = () => {
    dispatch(
      setSelectedDevice({
        selectedDevice: '',
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(({ key }) => key !== ITEMS.key.device),
      }),
    );
  };

  const handleDeleteSelectedCreditCard = () => {
    dispatch(
      setSelectedCreditCard({
        selectedCreditCard: '',
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.creditCard,
        ),
      }),
    );
  };

  const handleDeleteSelectedCreatedAt = () => {
    dispatch(
      setSelectedStartedAt({
        startedAt: null,
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.createdAt,
        ),
      }),
    );
  };

  const handleDeleteSelectedTotalLines = () => {
    dispatch(
      setSelectedTotalLines({
        selectedTotalLines: null,
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.totalLines,
        ),
      }),
    );
  };

  const handleDeleteSelectedTitle = () => {
    dispatch(
      setSelectedTitle({
        selectedTitle: '',
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(({ key }) => key !== ITEMS.key.title),
      }),
    );
  };

  const handleDeleteSelectedTicketer = () => {
    dispatch(
      setSelectedTicketer({
        selectedTicketer: null,
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.ticketer,
        ),
      }),
    );
  };

  const handleDeleteSelectedTerminalName = () => {
    dispatch(
      setSelectedTerminalName({
        selectedTerminalName: '',
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.terminalName,
        ),
      }),
    );
  };

  const handleDeleteSelectedTariff = () => {
    dispatch(
      setSelectedTariff({
        selectedTariff: null,
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(({ key }) => key !== ITEMS.key.tariff),
      }),
    );
  };

  const handleDeleteSelectedSpecialBusStop = () => {
    dispatch(
      setSelectedSpecialBusStop({
        selectedSpecialBusStop: null,
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.specialBusStop,
        ),
      }),
    );
  };

  const handleDeleteSelectedSitCapacity = () => {
    dispatch(
      setSelectedSitCapacity({
        selectedSitCapacity: '',
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.sitCapacity,
        ),
      }),
    );
  };

  const handleDeleteSelectedResponsible = () => {
    dispatch(
      setSelectedResponsible({
        selectedResponsible: '',
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.responsible,
        ),
      }),
    );
  };

  const handleDeleteSelectedRegistry = () => {
    dispatch(
      setSelectedRegistry({
        selectedRegistry: '',
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.registry,
        ),
      }),
    );
  };

  const handleDeleteSelectedPrefix = () => {
    dispatch(
      setSelectedPrefix({
        selectedPrefix: '',
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(({ key }) => key !== ITEMS.key.prefix),
      }),
    );
  };

  const handleDeleteSelectedNumber = () => {
    dispatch(
      setSelectedNumber({
        selectedNumber: '',
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(({ key }) => key !== ITEMS.key.number),
      }),
    );
  };

  const handleDeleteSelectedLot = () => {
    dispatch(
      setSelectedLot({
        selectedLot: '',
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(({ key }) => key !== ITEMS.key.lot),
      }),
    );
  };

  const handleDeleteSelectedModes = () => {
    dispatch(
      setSelectedLineModes({
        selectedLineModes: null,
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.lineModes,
        ),
      }),
    );
  };

  const handleDeleteSelectedModel = () => {
    dispatch(
      setSelectedLineModel({
        selectedLineModel: null,
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(({ key }) => key !== ITEMS.key.model),
      }),
    );
  };

  const handleDeleteSelectedLicenseNumber = () => {
    dispatch(
      setSelectedLicenseNumber({
        selectedLicenseNumber: '',
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.licenseNumber,
        ),
      }),
    );
  };

  const handleDeleteSelectedLicense = () => {
    dispatch(
      setSelectedLicense({
        selectedLicense: '',
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(({ key }) => key !== ITEMS.key.license),
      }),
    );
  };

  const handleDeleteSelectedFootCapacity = () => {
    dispatch(
      setSelectedFootCapacity({
        selectedFootCapacity: '',
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.footCapacity,
        ),
      }),
    );
  };

  const handleDeleteSelectedCovered = () => {
    dispatch(
      setSelectedCovered({
        selectedCovered: null,
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(({ key }) => key !== ITEMS.key.covered),
      }),
    );
  };

  const handleDeleteSelectedCarModel = () => {
    dispatch(
      setSelectedCarModel({
        selectedCarModel: '',
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(({ key }) => key !== ITEMS.key.model),
      }),
    );
  };

  const handleDeleteSelectedCardType = () => {
    dispatch(
      setSelectedCardType({
        selectedCardType: null,
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(({ key }) => key !== ITEMS.key.type),
      }),
    );
  };

  const handleDeleteSelectedCardStatus = () => {
    dispatch(
      setSelectedCardStatus({
        selectedCardStatus: null,
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(({ key }) => key !== ITEMS.key.status),
      }),
    );
  };

  const handleDeleteSelectedCardNumber = () => {
    dispatch(
      setSelectedCardNumber({
        selectedCardNumber: '',
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.cardNumber,
        ),
      }),
    );
  };

  const handleDeleteInvoiceListStatus = () => {
    dispatch(
      setSelectedInvoiceStatus({
        selectedInvoiceStatus: null,
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.invoicesListStatus,
        ),
      }),
    );
  };

  const handleDeleteSelectedAge = () => {
    dispatch(
      setSelectedAge({
        selectedAge: '',
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(({ key }) => key !== ITEMS.key.age),
      }),
    );
  };

  const handleDeleteSelectedBusBay = () => {
    dispatch(
      setSelectedBusBay({
        selectedBusBay: null,
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(({ key }) => key !== ITEMS.key.busBay),
      }),
    );
  };

  const handleDeleteSelectedBlockName = () => {
    dispatch(
      setSelectedBlockName({
        selectedBlockName: '',
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.blockName,
        ),
      }),
    );
  };

  const handleDeleteSelectedAirConditioned = () => {
    dispatch(
      setSelectedAirConditioned({
        selectedAirConditioned: null,
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.airConditioned,
        ),
      }),
    );
  };

  const handleDeleteSelectedAccessible = () => {
    dispatch(
      setSelectedAccessible({
        selectedAccessible: null,
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.accessible,
        ),
      }),
    );
  };

  const handleDeleteSelectedPeriod = () => {
    dispatch(
      setSelectedStartDate({
        selectedStartDate: null,
      }),
    );
    dispatch(
      setSelectedEndDate({
        selectedEndDate: null,
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(({ key }) => key !== ITEMS.key.period),
      }),
    );
  };

  const handleDeleteSelectedNumberOfDays = () => {
    dispatch(
      setSelectedNumberOfDays({
        numberOfDays: 0,
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.numberOfDays,
        ),
      }),
    );
  };

  const handleDeleteSelectedCars = () => {
    dispatch(
      setSelectedCars({
        selectedCars: [],
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(({ key }) => key !== ITEMS.key.cars),
      }),
    );
  };

  const handleDeleteSelectedCreditType = () => {
    dispatch(
      setSelectedCreditType({
        selectedCreditType: [],
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.creditType,
        ),
      }),
    );
  };

  const handleDeleteSelectedDateType = () => {
    dispatch(
      setSelectedDateType({
        selectedDateType: '',
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.dateType,
        ),
      }),
    );
  };

  const handleDeleteSelectedDateTypeBPe = () => {
    dispatch(
      setSelectedDateTypeBPe({
        selectedDateTypeBPe: '',
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.dateTypeBPe,
        ),
      }),
    );
  };

  const handleDeleteSelectedLineType = () => {
    dispatch(
      setSelectedLineType({
        selectedLineType: '',
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.lineType,
        ),
      }),
    );
  };

  const handleDeleteSelectedStatusDeviceRelease = () => {
    dispatch(
      setSelectedStatusDeviceRelease({
        selectedStatusDeviceRelease: null,
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.statusDeviceRelease,
        ),
      }),
    );
  };

  const handleDeleteSelectedEmployees = () => {
    dispatch(
      setSelectedEmployees({
        selectedEmployees: [],
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.employee,
        ),
      }),
    );
  };

  const handleDeleteSelectedDriverRegistry = () => {
    dispatch(
      setSelectedDriverRegistry({
        selectedDriverRegistry: [],
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.driverRegistry,
        ),
      }),
    );
  };

  const handleDeleteSelectedLines = () => {
    dispatch(
      setSelectedLines({
        selectedLines: [],
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(({ key }) => key !== ITEMS.key.lines),
      }),
    );
  };

  const handleDeleteSelectedSchools = () => {
    dispatch(
      setSelectedSchools({
        selectedSchools: [],
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(({ key }) => key !== ITEMS.key.schools),
      }),
    );
  };

  const handleDeleteSelectedPayments = () => {
    dispatch(
      setSelectedPayments({
        selectedPayments: [],
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(({ key }) => key !== ITEMS.key.payment),
      }),
    );
  };

  const handleDeleteSelectedQRCodes = () => {
    dispatch(
      setSelectedQRCodes({
        selectedQRCodes: [],
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(({ key }) => key !== ITEMS.key.qrCode),
      }),
    );
  };

  const handleDeleteSelectedSeccionedLines = () => {
    dispatch(
      setSeccionedLines({
        selectedSeccionedLine: null,
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.seccionedLines,
        ),
      }),
    );
  };

  const handleDeleteSelectedAddIdleKm = () => {
    dispatch(setSelectedAddIdleKm({ addIdleKm: null }));
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.addIdleKm,
        ),
      }),
    );
  };

  const handleDeleteSelectedSellingPoint = () => {
    dispatch(
      setSelectedSellingPoint({
        selectedSellingPoint: [],
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.sellingPoint,
        ),
      }),
    );
  };

  const handleDeleteSelectedSellingTerminal = () => {
    dispatch(
      setSelectedSellingTerminal({
        selectedSellingTerminal: [],
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.sellingTerminal,
        ),
      }),
    );
  };

  const handleDeleteSelectedSupervisors = () => {
    dispatch(
      setSelectedSupervisors({
        selectedSupervisors: [],
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.supervisor,
        ),
      }),
    );
  };

  const handleDeleteSelectedTokenStatus = () => {
    dispatch(
      setSelectedTokenStatus({
        selectedTokenStatus: null,
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.tokenStatus,
        ),
      }),
    );
  };

  const handleDeleteSelectedReceivePayments = () => {
    dispatch(
      setSelectedReceivePayments({
        selectedReceivePayments: [],
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(({ key }) => key !== ITEMS.key.receive),
      }),
    );
  };

  const handleDeleteSelectedTripStatus = () => {
    dispatch(
      setSelectedTripStatus({
        selectedTripStatus: [],
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.tripStatus,
        ),
      }),
    );
  };

  const handleDeleteSelectedTripOrTicket = () => {
    dispatch(
      setSelectedTripOrTicket({
        selectedTripOrTicket: '',
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.tripOrTicket,
        ),
      }),
    );
  };

  const handleDeleteSelectedQuantity = () => {
    dispatch(
      setSelectedQuantity({
        selectedQuantity: null,
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(({ key }) => key !== ITEMS.key.usage),
      }),
    );
  };

  const handleDeleteSelectedDbds = () => {
    dispatch(
      setSelectedDbds({
        selectedDbds: [],
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(({ key }) => key !== ITEMS.key.dbd),
      }),
    );
  };

  const handleDeleteSelectedApprovalStatus = () => {
    dispatch(
      setSelectedApprovalStatus({
        selectedApprovalStatus: [],
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.approvalStatus,
        ),
      }),
    );
  };

  const handleDeleteSelectedFacialStatus = () => {
    dispatch(
      setSelectedFacialStatus({
        selectedStatus: [],
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.facialStatus,
        ),
      }),
    );
  };
  const handleDeleteSelectedEmvStatus = () => {
    dispatch(
      setSelectedEmvStatus({
        selectedEmvStatus: '',
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.emvStatus,
        ),
      }),
    );
  };

  const handleDeleteSelectedScore = () => {
    dispatch(
      setSelectedScore({
        scorePercent: '',
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(({ key }) => key !== ITEMS.key.score),
      }),
    );
  };

  const handleDeleteSelectedValidationStatus = () => {
    dispatch(
      setSelectedValidationStatus({
        selectedValidationStatus: [],
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.validationStatus,
        ),
      }),
    );
  };

  const handleDeleteSelectedChangeKmType = () => {
    dispatch(
      changeKmType({
        kmType: '',
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(({ key }) => key !== ITEMS.key.kmType),
      }),
    );
  };

  const handleDeleteSelectedToggleVoucher = () => {
    dispatch(
      setToggleVoucher({
        voucher: null,
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(({ key }) => key !== ITEMS.key.voucher),
      }),
    );
  };

  const handleDeleteSelectedSchoolCode = () => {
    dispatch(
      setSelectedSchoolCode({
        selectedSchoolCode: null,
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.schoolCode,
        ),
      }),
    );
  };

  const handleDeleteSelectedChangeBalanceType = () => {
    dispatch(
      changeBalanceType({
        balanceType: [],
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.balanceType,
        ),
      }),
    );
  };

  const handleDeleteSelectedSearchUser = () => {
    dispatch(
      setSelectedSearchUser({
        selectedSearchUser: [],
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(({ key }) => key !== ITEMS.key.user),
      }),
    );
  };

  const handleDeleteSelectedFilename = () => {
    dispatch(
      setSelectedFilename({
        filenameSelected: '',
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.filename,
        ),
      }),
    );
  };

  const handleDeleteSelectedCollection = () => {
    dispatch(
      setSelectedCollection({
        selectedCollection: null,
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.collection,
        ),
      }),
    );
  };

  const handleDeleteSelectedPaymentType = () => {
    dispatch(
      setSelectedPaymentType({
        selectedPaymentType: [],
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.paymentType,
        ),
      }),
    );
  };

  const handleDeleteSelectedPaymentStatus = () => {
    dispatch(
      setSelectedPaymentStatus({
        selectedPaymentStatus: [],
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.paymentStatus,
        ),
      }),
    );
  };

  const handleDeleteSelectedOperationType = () => {
    dispatch(
      setSelectedOperationType({
        selectedOperationType: [],
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.operation,
        ),
      }),
    );
  };

  const handleDeleteSelectedCompanyStatus = () => {
    dispatch(
      setSelectedCompanyStatus({
        selectedStatus: '',
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.voucherCollectionStatus,
        ),
      }),
    );
  };

  const handleDeleteSelectedCompanyType = () => {
    dispatch(
      setSelectedCompanyType({
        selectedType: [],
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.voucherCollectionType,
        ),
      }),
    );
  };

  const handleDeleteSelectedIncomingTypes = () => {
    dispatch(
      setSelectedIncomingTypes({
        selectedIncomingTypes: [],
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.incomingTypes,
        ),
      }),
    );
  };

  const handleDeleteSelectedEmvType = () => {
    dispatch(
      setSelectedEmvType({
        selectedEmvType: [],
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(({ key }) => key !== ITEMS.key.emvType),
      }),
    );
  };
  const handleDeleteSelectedBrand = () => {
    dispatch(
      setSelectedBrand({
        selectedBrand: [],
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(({ key }) => key !== ITEMS.key.brand),
      }),
    );
  };
  const handleDeleteSelectedSchoolType = () => {
    dispatch(
      setSelectedSchoolType({
        selectedSchoolType: [],
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.schoolType,
        ),
      }),
    );
  };

  const handleDeleteSelectedReceivingType = () => {
    dispatch(setSelectedReceivingType({}));
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.receivingType,
        ),
      }),
    );
  };

  const handleDeleteSelectedTerminals = () => {
    dispatch(
      setSelectedTerminals({
        selectedTerminals: [],
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.terminal,
        ),
      }),
    );
  };

  const handleDeleteSelectedCompanies = () => {
    dispatch(
      setSelectedCompanies({
        selectedCompanies: [],
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.companies,
        ),
      }),
    );
  };

  const handleDeleteSelectedPaymentDay = () => {
    dispatch(
      setSelectedPaymentDay({
        selectedPaymentDay: null,
      }),
    );
    dispatch(
      setFilteredItems({
        filterItems: filterItems.filter(
          ({ key }) => key !== ITEMS.key.paymentDay,
        ),
      }),
    );
  };

  const handleCancel = () => dispatch(setClose({}));

  const getStatusLabel = (status: number) =>
    CONST_LINES.STATUS.find(({ KEY }) => KEY === status)?.VALUE.toLowerCase();

  const getProfileStatusLabel = (status: number) =>
    PROFILES_CONST.STATUS.find(({ KEY }) => KEY === status)?.VALUE;

  return (
    <ModalTemplate
      hideCloseIcon
      classes={{
        container: classes.resetPadding,
        root: classes.resetPadding,
      }}>
      <Grid
        container
        justifyContent='space-between'
        wrap='nowrap'
        className={classes.cardContainer}>
        <Grid item className={classes.card}>
          <List dense className={classes.list}>
            {fields.map(({ key, value }) => (
              <ListItem
                key={key}
                label='Campos'
                title={value}
                handleClick={() => handleType(key)}
                className={clsx({ [classes.active]: key === type })}
              />
            ))}
          </List>
        </Grid>
        <Grid
          item
          className={[classes.card, classes.addMargin, classes.overflow].join(
            ' ',
          )}>
          <Suspense fallback={<p>Carregando...</p>}>
            <Component />
          </Suspense>
        </Grid>
        <Grid item className={[classes.card, classes.addMargin].join(' ')}>
          <Grid
            container
            justifyContent='flex-end'
            alignItems='center'
            className={classes.selectedCount}>
            <Typography>{filterItems.length} filtro selecionado</Typography>
          </Grid>
          <List dense className={classes.list}>
            {selectedName && (
              <ListItem
                title={`Nome: ${selectedName}`}
                label='Deletar nome'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedName}
              />
            )}
            {selectedBpeCode && (
              <ListItem
                title={`Código BPE: ${selectedBpeCode}`}
                label='Deletar Código BPE'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedBpeCode}
              />
            )}
            {selectedOrigin && (
              <ListItem
                title={`Origem: ${selectedOrigin}`}
                label='Deletar Origem'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedOrigin}
              />
            )}
            {selectedDestiny && (
              <ListItem
                title={`Destino: ${selectedDestiny}`}
                label='Deletar Destino'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedDestiny}
              />
            )}
            {selectedBpeNumber && (
              <ListItem
                title={`Número BPE: ${selectedBpeNumber}`}
                label='Deletar Número BPE'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedBpeNumber}
              />
            )}
            {selectedTicketIdCode && (
              <ListItem
                title={`Código da Passagem: ${selectedTicketIdCode}`}
                label='Deletar Código da Passagem'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedTicketIdCode}
              />
            )}
            {selectedSaleIdCode && (
              <ListItem
                title={`Código da Venda: ${selectedSaleIdCode}`}
                label='Deletar Código da Venda'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedSaleIdCode}
              />
            )}
            {selectedIncludeBpe && (
              <ListItem
                title={`Contém BPE: ${BPE.INCLUDE_BPE_TYPE.find(
                  (item) => selectedIncludeBpe === item.KEY,
                )?.LABEL ?? ''}`}
                label='Deletar Inlusão de BPE'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedIncludeBpe}
              />
            )}
            {selectedLineSectionName && (
              <ListItem
                title={`Nome do Trecho: ${selectedLineSectionName}`}
                label='Deletar Nome do Trecho'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedLineSectionName}
              />
            )}
            {selectedBenefits.length > 0 && (
              <ListItem
                title={`Beneficio: ${selectedBenefits.map(({ name }) => name)}`}
                label='Deletar Beneficio'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedBenefits}
              />
            )}
            {selectedCNPJ && (
              <ListItem
                title={`CNPJ: ${maskCNPJ(selectedCNPJ)}`}
                label='Deletar CNPJ'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedCNPJ}
              />
            )}
            {selectedNSU && (
              <ListItem
                title={`NSU: ${selectedNSU}`}
                label='Deletar NSU'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedNSU}
              />
            )}
            {selectedPhone && (
              <ListItem
                title={`Telefone: ${maskPhoneDefault(selectedPhone)}`}
                label='Deletar Telefone'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedPhone}
              />
            )}
            {selectedProfiles?.length > 0 && (
              <ListItem
                title={`Perfil: ${selectedProfiles?.[0]?.name}`}
                label='Deletar perfil selecionado'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedProfile}
              />
            )}
            {selectedProfileExpiration && (
              <ListItem
                title={`Expiração do Perfil: ${format(
                  selectedProfileExpiration?.getTime(),
                  'dd/MM/yyyy',
                )}`}
                label='Deletar Expiração do Perfil'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedProfileExpiration}
              />
            )}
            {selectedProfileStatus !== null && (
              <ListItem
                title={`Status: ${getProfileStatusLabel(
                  selectedProfileStatus,
                )}`}
                label='Deletar Status'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedProfileStatus}
              />
            )}
            {selectedStatus !== null && (
              <ListItem
                title={`Status: ${getStatusLabel(selectedStatus)}`}
                label='Deletar Status'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedStatus}
              />
            )}
            {selectedEmvStatus &&
              selectedEmvStatus !== null &&
              selectedEmvStatus !== undefined && (
                <ListItem
                  title={`Status: ${
                    EMV.STATUS.find(({ VALUE }) => VALUE === selectedEmvStatus)
                      ?.LABEL
                  }`}
                  label='Deletar Status'
                  classes={{
                    iconButton: classes.iconButton,
                  }}
                  icon={
                    <CloseIcon size='small' className={classes.closeIcon} />
                  }
                  handleClick={handleDeleteSelectedEmvStatus}
                />
              )}
            {selectedDocument && (
              <ListItem
                title={`Documento: ${selectedDocument}`}
                label='Deletar documento'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedDocument}
              />
            )}
            {selectedTransportCard && (
              <ListItem
                title={`Cartão de Transporte: ${selectedTransportCard}`}
                label='Deletar Cartão de Transporte'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedTransportCard}
              />
            )}
            {selectedDevice && (
              <ListItem
                title={`Dispositivo: ${selectedDevice}`}
                label='Deletar Dispositivo'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedDevice}
              />
            )}
            {selectedCreditCard && (
              <ListItem
                title={`Cartão de Crédito: ${selectedCreditCard}`}
                label='Deletar Cartão de Crédito'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedCreditCard}
              />
            )}
            {selectedEmail && (
              <ListItem
                title={`Email: ${selectedEmail}`}
                label='Deletar email'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedEmail}
              />
            )}
            {startedAt && (
              <ListItem
                title={`Data de cadastro: ${format(
                  startedAt?.getTime(),
                  'dd/MM/yyyy',
                )}`}
                label='Deletar data de cadastro'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedCreatedAt}
              />
            )}
            {selectedTotalLines !== null && (
              <ListItem
                title={`Total de Linhas: ${selectedTotalLines}`}
                label='Deletar total de Linhas'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedTotalLines}
              />
            )}
            {selectedTitle && (
              <ListItem
                title={`Titulo: ${selectedTitle}`}
                label='Deletar Titulo'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedTitle}
              />
            )}
            {selectedTicketer !== null && (
              <ListItem
                title={`Espaço para cobrador: ${
                  CONST_FLEET.TICKETER.find(
                    ({ KEY }) => KEY === selectedTicketer,
                  )?.VALUE
                }`}
                label='Deletar Espaço para cobrador'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedTicketer}
              />
            )}
            {selectedTerminalName && (
              <ListItem
                title={`Nome do terminal: ${selectedTerminalName}`}
                label='Deletar nome do terminal'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedTerminalName}
              />
            )}
            {selectedTariff !== null && (
              <ListItem
                title={`Tarifa: ${selectedTariff}`}
                label='Deletar Tarifa'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedTariff}
              />
            )}
            {selectedSpecialBusStop !== null && (
              <ListItem
                title={`Ponto Especial: ${
                  CONST_STOPS.SPECIAL_BUS_STOP.find(
                    ({ KEY }) => KEY === selectedSpecialBusStop,
                  )?.VALUE
                }`}
                label='Deletar Ponto Especial'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedSpecialBusStop}
              />
            )}
            {selectedSitCapacity && (
              <ListItem
                title={`Capacidade sentados: ${selectedSitCapacity}`}
                label='Deletar Capacidade sentados'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedSitCapacity}
              />
            )}
            {selectedFootCapacity && (
              <ListItem
                title={`Capacidade em pé: ${selectedFootCapacity}`}
                label='Deletar Capacidade em pé'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedFootCapacity}
              />
            )}
            {selectedResponsible && (
              <ListItem
                title={`Responsável: ${selectedResponsible}`}
                label='Deletar Responsável'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedResponsible}
              />
            )}
            {selectedRegistry && (
              <ListItem
                title={`Matrícula: ${selectedRegistry}`}
                label='Deletar Matrícula'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedRegistry}
              />
            )}
            {selectedPrefix && (
              <ListItem
                title={`Prefixo: ${selectedPrefix}`}
                label='Deletar Prefixo'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedPrefix}
              />
            )}
            {selectedNumber && (
              <ListItem
                title={`Número: ${selectedNumber}`}
                label='Deletar Número'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedNumber}
              />
            )}
            {selectedLot && (
              <ListItem
                title={`Lote: ${selectedLot}`}
                label='Deletar Lote'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedLot}
              />
            )}
            {selectedLineModes !== null && (
              <ListItem
                title={`Modo: ${
                  CONST_LINES.MODES.find(({ KEY }) => KEY === selectedLineModes)
                    ?.VALUE
                }`}
                label='Deletar Modo'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedModes}
              />
            )}
            {selectedLineModel !== null && (
              <ListItem
                title={`Modelo: ${
                  CONST_LINES.TARIFF_PROFILE.find(
                    ({ KEY }) => KEY === selectedLineModel,
                  )?.VALUE
                }`}
                label='Deletar Modelo'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedModel}
              />
            )}
            {selectedLicenseNumber && (
              <ListItem
                title={`Número da Licensa: ${selectedLicenseNumber}`}
                label='Deletar Número da Licensa'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedLicenseNumber}
              />
            )}
            {selectedLicense && (
              <ListItem
                title={`Licensa: ${selectedLicense}`}
                label='Deletar Licensa'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedLicense}
              />
            )}
            {selectedCovered && (
              <ListItem
                title={`Coberto: ${
                  CONST_STOPS.COVERED.find(({ KEY }) => KEY === selectedCovered)
                    ?.VALUE
                }`}
                label='Deletar Coberto'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedCovered}
              />
            )}
            {selectedCarModel && (
              <ListItem
                title={`Modelo do carro: ${
                  CONST_FLEET.MODELS.find(({ KEY }) => KEY === selectedCarModel)
                    ?.VALUE
                }`}
                label='Deletar Modelo do carro'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedCarModel}
              />
            )}
            {selectedCardType !== null && (
              <ListItem
                title={`Tipo de cartão: ${
                  TRANSPORT_CARD_CONST.TYPES.find(
                    ({ KEY }) => KEY === selectedCardType,
                  )?.VALUE
                }`}
                label='Deletar Tipo de cartão'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedCardType}
              />
            )}
            {selectedCardStatus !== null && (
              <ListItem
                title={`Status do cartão: ${
                  TRANSPORT_CARD_CONST.STATUS.find(
                    ({ KEY }) => KEY === selectedCardStatus,
                  )?.VALUE
                }`}
                label='Deletar Status do cartão'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedCardStatus}
              />
            )}
            {selectedCardNumber && (
              <ListItem
                title={`Número do cartão: ${selectedCardNumber}`}
                label='Deletar Número do cartão'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedCardNumber}
              />
            )}
            {typeof selectedInvoiceStatus === 'number' && (
              <ListItem
                title={`Status: ${
                  INVOICES_CONST.STATUS.find(
                    ({ KEY }) => KEY === selectedInvoiceStatus,
                  )?.VALUE
                }`}
                label='Deletar Status'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteInvoiceListStatus}
              />
            )}
            {selectedAge && (
              <ListItem
                title={`Idade do veículo: ${selectedAge}`}
                label='Deletar Idade do veículo'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedAge}
              />
            )}
            {selectedBusBay && (
              <ListItem
                title={`Baia: ${
                  CONST_STOPS.BUSBAY.find(({ KEY }) => KEY === selectedBusBay)
                    ?.VALUE
                }`}
                label='Deletar Baia'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedBusBay}
              />
            )}
            {selectedBlockName && (
              <ListItem
                title={`Bloqueio: ${selectedBlockName}`}
                label='Deletar Bloqueio'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedBlockName}
              />
            )}
            {selectedAirConditioned !== null && (
              <ListItem
                title={`Ar condicionado: ${
                  CONST_FLEET.AIR_CONDITIONED.find(
                    ({ KEY }) => KEY === selectedAirConditioned,
                  )?.VALUE
                }`}
                label='Deletar Ar condicionado'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedAirConditioned}
              />
            )}
            {selectedAccessible !== null && (
              <ListItem
                title={`Acessível: ${
                  CONST_FLEET.ACCESSIBLE.find(
                    ({ KEY }) => KEY === selectedAccessible,
                  )?.VALUE
                }`}
                label='Deletar Acessível'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedAccessible}
              />
            )}
            {selectedStartDate?.getTime() < selectedEndDate?.getTime() && (
              <ListItem
                title={`Período:
                ${format(selectedStartDate.getTime(), 'dd/MM/yyyy HH:mm')}
                a
                ${format(selectedEndDate.getTime(), 'dd/MM/yyyy HH:mm')}`}
                label='Deletar Período'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedPeriod}
              />
            )}
            {selectedNumberOfDays > 0 && (
              <ListItem
                title={`Período de: ${
                  AMOUNT_OF_DAYS.STATUS.find(
                    ({ NUM_DAYS }) => NUM_DAYS === selectedNumberOfDays,
                  )?.LABEL
                }`}
                label='Deletar Período'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedNumberOfDays}
              />
            )}
            {!!selectedApprovalStatus.length && (
              <ListItem
                title={`Status: ${selectedApprovalStatus
                  .map(
                    (e) =>
                      Object.values(FACIAL.APPROVAL).find(
                        ({ VALUE }) => e === VALUE,
                      )?.LABEL || '',
                  )
                  .join()}`}
                label='Deletar Status'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedApprovalStatus}
              />
            )}
            {selectedBalanceType?.length > 0 && (
              <ListItem
                title={`Tipo de Saldo: ${selectedBalanceType.map(
                  ({ value }) => value,
                )}`}
                label='Deletar Tipo de Saldo'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedChangeBalanceType}
              />
            )}
            {selectedCollection !== null && (
              <ListItem
                title={`Arrecadação: ${
                  CONST_COLLECTION.TYPE.find(
                    ({ KEY }) => KEY === selectedCollection,
                  )?.VALUE
                }`}
                label='Deletar Arrecadação'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedCollection}
              />
            )}
            {selectedCars?.length > 0 && (
              <ListItem
                title={`${TITLES.carTitle}: ${selectedCars?.map(
                  ({ license }) => license,
                )}`}
                label={`Deletar ${TITLES.carTitle}`}
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedCars}
              />
            )}
            {selectedCreditType?.length > 0 && (
              <ListItem
                title={`Tipo de Crédito: ${selectedCreditType?.map(
                  ({ value }) => value,
                )}`}
                label='Deletar Tipo de Crédito'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedCreditType}
              />
            )}
            {selectedDateType !== '' && selectedDateType && (
              <ListItem
                title={`Tipo de Data: ${
                  Object.values(DATE_TYPE_SALES_REPORT.TYPE).find(
                    (s) => s.KEY === selectedDateType,
                  )?.VALUE
                }`}
                label='Deletar Tipo de Data'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedDateType}
              />
            )}
            {selectedDateTypeBPe !== '' && selectedDateTypeBPe && (
              <ListItem
                title={`Tipo de Data: ${
                  Object.values(BPE.DATE_TYPE).find(
                    (s) => s.KEY === selectedDateTypeBPe,
                  )?.LABEL
                }`}
                label='Deletar Tipo de Data'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedDateTypeBPe}
              />
            )}
            {selectedLineType !== 'fullLine' && selectedLineType && (
              <ListItem
                title={`Tipo de Linha: ${
                  Object.values(LINE_TYPE_SALES_REPORT.TYPE).find(
                    (s) => s.KEY === selectedLineType,
                  )?.VALUE
                }`}
                label='Deletar Tipo de Linha'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedLineType}
              />
            )}
            {selectedStatusDeviceRelease !== null && (
              <ListItem
                title={`Status: ${
                  Object.values(DEVICE_RELEASE_STATUS.TYPE).find(
                    (s) => s.KEY === selectedStatusDeviceRelease,
                  )?.VALUE
                }`}
                label='Deletar Status'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedStatusDeviceRelease}
              />
            )}
            {selectedDbds?.length > 0 && (
              <ListItem
                title={`DBDs: ${selectedDbds.map(({ serial }) => serial)}`}
                label='Deletar DBDs'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedDbds}
              />
            )}
            {selectedEmployees?.length > 0 && (
              <ListItem
                title={`Motorista: ${selectedEmployees.map(
                  ({ name }) => name,
                )}`}
                label='Deletar Motorista'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedEmployees}
              />
            )}
            {selectedDriverRegistry?.length > 0 && (
              <ListItem
                title={`Matrícula do Motorista: ${selectedDriverRegistry.map(
                  ({ name }) => name,
                )}`}
                label='Deletar Matrícula do Motorista'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedDriverRegistry}
              />
            )}
            {!!selectedFacialStatus.length && (
              <ListItem
                title={`Facial Status: ${selectedFacialStatus
                  .map(
                    (e) =>
                      Object.values(FACIAL.STATUS).find(
                        ({ VALUE }) => e === VALUE,
                      )?.LABEL || '',
                  )
                  .join()}`}
                label='Deletar Status'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedFacialStatus}
              />
            )}
            {selectedFilename !== '' && (
              <ListItem
                title={`Nome do arquivo: ${selectedFilename}`}
                label='Deletar Nome do arquivo'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedFilename}
              />
            )}
            {selectedKmType && (
              <ListItem
                title={`Tipo de Quilometragem: ${
                  KM_TYPE.TYPES.find(({ KEY }) => KEY === selectedKmType)?.VALUE
                }`}
                label='Deletar Tipo de Quilometragem'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedChangeKmType}
              />
            )}
            {selectedLines?.length > 0 && (
              <ListItem
                title={`Linhas: ${selectedLines.map(({ name }) => name)}`}
                label='Deletar Linhas'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedLines}
              />
            )}
            {selectedSchools?.length > 0 && (
              <ListItem
                title={`Escolas: ${selectedSchools.map(
                  (school: any) => school.name,
                )}`}
                label='Deletar Escolas'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedSchools}
              />
            )}
            {selectedPayments?.length > 0 && (
              <ListItem
                title={`Forma de Embarque: ${selectedPayments.map(
                  ({ value }) => value,
                )}`}
                label='Deletar Forma de Embarque'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedPayments}
              />
            )}
            {selectedPaymentType?.length > 0 && (
              <ListItem
                title={`Tipo do Pagamento: ${selectedPaymentType.map(
                  ({ VALUE }) => VALUE,
                )}`}
                label='Deletar Tipo do Pagamento'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedPaymentType}
              />
            )}
            {selectedPaymentStatus?.length > 0 && (
              <ListItem
                title={`Status do Pagamento: ${selectedPaymentStatus.map(
                  ({ value }) => value,
                )}`}
                label='Deletar Status do Pagamento'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedPaymentStatus}
              />
            )}
            {selectedOperationType?.length > 0 && (
              <ListItem
                title={`Operação: ${selectedOperationType?.map(
                  ({ value }) => value,
                )}`}
                label='Deletar operação'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedOperationType}
              />
            )}
            {selectedQRCodes?.length > 0 && (
              <ListItem
                title={`Tipo do QRCode: ${selectedQRCodes.map(
                  ({ value }) => value,
                )}`}
                label='Deletar Tipo do QRCode'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedQRCodes}
              />
            )}
            {selectedReceivePayments?.length > 0 && (
              <ListItem
                title={`Pagamento recebido de: ${selectedReceivePayments.map(
                  ({ value }) => value,
                )}`}
                label='Deletar Pagamento recebido'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedReceivePayments}
              />
            )}
            {selectedTripStatus?.length > 0 && (
              <ListItem
                title={`Status da viagem: ${selectedTripStatus.map(
                  ({ value }) => value,
                )}`}
                label='Deletar Status da viagem'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedTripStatus}
              />
            )}
            {selectedTripOrTicket && (
              <ListItem
                title={`Tipo de Filtro: ${
                  Object.values(TRIP_OR_TICKET.TYPE).find(
                    (s) => s.KEY === selectedTripOrTicket,
                  )?.VALUE
                }`}
                label='Deletar filtro'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedTripOrTicket}
              />
            )}
            {selectedReceivingType !== undefined && (
              <ListItem
                title={`Tipo de recebimento: ${
                  CONST_SELLING_POINTS.TRANSACTION_TYPES.find(
                    ({ KEY }) => KEY === selectedReceivingType,
                  )?.VALUE
                }`}
                label='Deletar Tipo de recebimento'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedReceivingType}
              />
            )}
            {selectedScore !== '' && (
              <ListItem
                title={`Score: ${selectedScore}`}
                label='Deletar Score'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedScore}
              />
            )}
            {selectedSeccionedLine !== null && (
              <ListItem
                title={`Filtro por Trecho: ${
                  Object.values(SECTIONS.STATUS).find(
                    ({ VALUE }) => VALUE === selectedSeccionedLine,
                  )?.LABEL
                }`}
                label='Deletar Filtro por Trecho'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedSeccionedLines}
              />
            )}
            {selectedAddIdleKm === true && (
              <ListItem
                title='Quilometragem: Total e Ocioso'
                label='Deletar Quilometragem'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedAddIdleKm}
              />
            )}
            {selectedSellingPoint?.length > 0 && (
              <ListItem
                title={`Ponto de venda: ${selectedSellingPoint.map(
                  (p) => p.name,
                )}`}
                label='Deletar Ponto de venda'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedSellingPoint}
              />
            )}
            {selectedSellingTerminal?.length > 0 && (
              <ListItem
                title={`Rede de venda: ${selectedSellingTerminal.map(
                  (p) => p.name,
                )}`}
                label='Deletar Rede de venda'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedSellingTerminal}
              />
            )}
            {selectedSupervisors?.length > 0 && (
              <ListItem
                title={`Supervisores: ${selectedSupervisors.map(
                  ({ name }) => name,
                )}`}
                label='Deletar Supervisores'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedSupervisors}
              />
            )}
            {selectedTokenStatus !== null && (
              <ListItem
                title={`Status: ${selectedTokenStatus.value}`}
                label='Deletar Status'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedTokenStatus}
              />
            )}
            {selectedQuantity !== null && (
              <ListItem
                title={`Quantidade de uso: ${selectedQuantity}`}
                label='Deletar Quantidade de uso'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedQuantity}
              />
            )}
            {selectedSearchUser?.length > 0 && (
              <ListItem
                title={`Usuário: ${selectedSearchUser.map(({ name }) => name)}`}
                label='Deletar Usuário'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedSearchUser}
              />
            )}
            {selectedValidationStatus?.length > 0 && (
              <ListItem
                title={`Status: ${Object.values(FACIAL.VALIDATION_STATUS_FILTER)
                  .filter(({ VALUE }) =>
                    selectedValidationStatus.includes(VALUE),
                  )
                  .map(({ LABEL }) => LABEL)
                  .join()}`}
                label='Deletar Status'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedValidationStatus}
              />
            )}
            {selectedVoucher !== null && (
              <ListItem
                title={`Voucher: ${Boolean(selectedVoucher)}`}
                label='Deletar Voucher'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedToggleVoucher}
              />
            )}
            {selectedCompanyStatus !== '' && (
              <ListItem
                title={`Status: ${
                  CONST_TRANSPORT_VOUCHER_COLLECTION.STATUS.find(
                    ({ KEY }) => KEY === selectedCompanyStatus,
                  )?.VALUE
                }`}
                label='Deletar Status'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedCompanyStatus}
              />
            )}
            {selectedCompanyType?.length > 0 && (
              <ListItem
                title={`Tipo de Voucher: ${selectedCompanyType.map(
                  ({ VALUE }) => VALUE,
                )}`}
                label='Deletar Tipo de Voucher'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedCompanyType}
              />
            )}
            {selectedIncomingTypes?.length > 0 && (
              <ListItem
                title={`Tipo de Realtorio de Arrecadação: ${selectedIncomingTypes.map(
                  ({ VALUE }) => VALUE,
                )}`}
                label='Deletar Tipo de Realtorio de Arrecadação'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedIncomingTypes}
              />
            )}
            {selectedTerminals?.length > 0 && (
              <ListItem
                title={`Terminais: ${selectedTerminals?.map(
                  ({ name }) => name,
                )}`}
                label='Deletar Teminal'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedTerminals}
              />
            )}
            {selectedEmvType.length > 0 && (
              <ListItem
                title={`Tipo da transação: ${selectedEmvType.map(
                  ({ VALUE }) => VALUE,
                )}`}
                label='Deletar Tipo da transação'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedEmvType}
              />
            )}
            {selectedBrand.length > 0 && (
              <ListItem
                title={`Bandeiras: ${selectedBrand.map(({ VALUE }) => VALUE)}`}
                label='Deletar Bandeiras'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedBrand}
              />
            )}
            {selectedSchoolCode !== null && (
              <ListItem
                title={`Código: ${selectedSchoolCode}`}
                label='Deletar Código'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedSchoolCode}
              />
            )}
            {selectedSchoolType.length > 0 && (
              <ListItem
                title={`Tipo: ${selectedSchoolType.map(({ value }) => value)}`}
                label='Deletar Tipo'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedSchoolType}
              />
            )}
            {selectedCompanies.length > 0 && (
              <ListItem
                title={`Empresa: ${selectedCompanies.map(({ name }) => name)}`}
                label='Deletar empresa'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedCompanies}
              />
            )}
            {selectedPaymentDay && (
              <ListItem
                title={`Data do pagamento: ${format(
                  selectedPaymentDay?.getTime(),
                  'dd/MM/yyyy',
                )}`}
                label='Deletar data do pagamento'
                classes={{
                  iconButton: classes.iconButton,
                }}
                icon={<CloseIcon size='small' className={classes.closeIcon} />}
                handleClick={handleDeleteSelectedPaymentDay}
              />
            )}
          </List>
        </Grid>
      </Grid>
      <Grid container justifyContent='flex-end' alignItems='center'>
        <Grid item>
          <Button
            variant='text'
            className={[classes.button, classes.cancelButton].join(' ')}
            onClick={handleCancel}>
            cancelar
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant='text'
            className={classes.button}
            onClick={() =>
              handleApply({
                selectedItems,
                selectedProfiles,
                selectedName,
                selectedBpeCode,
                selectedBpeNumber,
                selectedIncludeBpe,
                selectedOrigin,
                selectedDestiny,
                selectedLineSectionName,
                selectedBenefits,
                selectedCNPJ,
                selectedNSU,
                selectedDocument,
                selectedEmail,
                selectedPhone,
                selectedProfileExpiration,
                selectedProfileStatus,
                selectedStatus,
                selectedEmvStatus,
                selectedTransportCard,
                startedAt,
                selectedTotalLines,
                selectedTitle,
                selectedTicketer,
                selectedTerminalName,
                selectedTariff,
                selectedSpecialBusStop,
                selectedSitCapacity,
                selectedResponsible,
                selectedRegistry,
                selectedPrefix,
                selectedNumber,
                selectedLot,
                selectedLineModes,
                selectedLineModel,
                selectedLicenseNumber,
                selectedLicense,
                selectedFootCapacity,
                selectedCovered,
                selectedCarModel,
                selectedCardType,
                selectedCardStatus,
                selectedCardNumber,
                selectedAge,
                selectedBusBay,
                selectedBlockName,
                selectedAirConditioned,
                selectedAccessible,
                selectedNumberOfDays,
                selectedApprovalStatus,
                selectedBalanceType,
                selectedCollection,
                selectedCars,
                selectedCreditType,
                selectedDateType,
                selectedLineType,
                selectedStatusDeviceRelease,
                selectedDbds,
                selectedEmployees,
                selectedDriverRegistry,
                selectedFacialStatus,
                selectedFilename,
                selectedKmType,
                selectedAddIdleKm,
                selectedLines,
                selectedSchools,
                selectedPayments,
                selectedPaymentType,
                selectedPaymentStatus,
                selectedOperationType,
                startDate: selectedStartDate,
                endDate: selectedEndDate,
                selectedQRCodes,
                selectedReceivePayments,
                selectedTripStatus,
                selectedReceivingType,
                selectedScore,
                selectedSeccionedLine,
                selectedSellingPoint,
                selectedSellingTerminal,
                selectedSupervisors,
                selectedTokenStatus,
                selectedQuantity,
                selectedSearchUser,
                selectedValidationStatus,
                selectedVoucher,
                selectedCompanyStatus,
                selectedCompanyType,
                selectedIncomingTypes,
                selectedDevice,
                selectedCreditCard,
                selectedTerminals,
                selectedEmvType,
                selectedBrand,
                selectedSchoolCode,
                selectedSchoolType,
                selectedInvoiceStatus,
                selectedTripOrTicket,
                selectedCompanies,
                selectedPaymentDay,
                selectedDateTypeBPe,
                selectedTicketIdCode,
                selectedSaleIdCode,
              })
            }>
            aplicar
          </Button>
        </Grid>
      </Grid>
    </ModalTemplate>
  );
};

export { Filter };
export default Filter;
